import { useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/** Mouse-event handlers that make a non-anchor element (e.g. a table row) behave like a link. */
export interface RowLinkProps {
  onClick: (event: MouseEvent) => void;
  onAuxClick: (event: MouseEvent) => void;
}

/** Selector for interactive descendants whose own clicks must not trigger row navigation. */
const INTERACTIVE = 'a, button, input, select, textarea, label, [data-no-row-link]';

/**
 * Returns a factory that builds link-like mouse handlers for a clickable list row.
 *
 * Plain left-click navigates in-app to `to` (so the address bar shows the full URL of
 * whatever was opened). Ctrl/Cmd/Shift-click and middle-click open `to` in a new browser
 * tab — matching the native behavior users expect from real links. Clicks that originate
 * on an interactive child (button, link, input, …) are ignored so row controls keep working.
 *
 * Usage:
 *   const rowLink = useRowLink();
 *   <tr {...rowLink(`/tickets/${ticket.id}`)}>…</tr>
 */
export function useRowLink(): (to: string) => RowLinkProps {
  const navigate = useNavigate();

  return useCallback(
    (to: string): RowLinkProps => ({
      onClick: (event: MouseEvent) => {
        if (event.defaultPrevented) return;
        if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          window.open(to, '_blank', 'noopener,noreferrer');
          return;
        }
        navigate(to);
      },
      onAuxClick: (event: MouseEvent) => {
        // Middle-click (button 1) opens in a new tab, like a real link.
        if (event.button !== 1) return;
        if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
        event.preventDefault();
        window.open(to, '_blank', 'noopener,noreferrer');
      },
    }),
    [navigate],
  );
}
